import { useEffect, useState, type ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAppStore } from "@/store/app-store";
import { api } from "@/lib/api-client";
import { LoginPage } from "@/features/auth/LoginPage";
import { WelcomePage } from "@/features/auth/WelcomePage";
import { useBackCloseable } from "@/lib/modal-stack";
import { WorkspaceShell } from "@/components/layout/WorkspaceShell";
import { PropertyDashboardPage } from "@/features/dashboard/PropertyDashboardPage";
import { ResourcePage } from "@/features/resources/ResourcePage";
import { ReportsPage } from "@/features/reports/ReportsPage";
import { SettlementsPage } from "@/features/settlements/SettlementsPage";
import { TeamPage } from "@/features/team/TeamPage";
import { ActivityPage } from "@/features/activity/ActivityPage";
import { PoliceRegisterPage } from "@/features/register/PoliceRegisterPage";
import { FirstLoginPasswordPage } from "@/features/auth/FirstLoginPasswordPage";
import { InventoryPage } from "@/features/inventory/InventoryPage";
import { OwnerSetupPage } from "@/features/setup/OwnerSetupPage";
import { OwnersPage } from "@/features/owners/OwnersPage";
import { PropertyPickerPage } from "@/features/properties/PropertyPickerPage";
import { SettingsLayout } from "@/features/settings/SettingsLayout";
import { GeneralSettingsPage } from "@/features/settings/GeneralSettingsPage";
import { CategoriesSettingsPage } from "@/features/settings/CategoriesSettingsPage";
import { RoomTypesSettingsPage } from "@/features/settings/RoomTypesSettingsPage";
import { RatePlansSettingsPage } from "@/features/settings/RatePlansSettingsPage";
import { ServicesSettingsPage } from "@/features/settings/ServicesSettingsPage";
import { AmenitiesSettingsPage } from "@/features/settings/AmenitiesSettingsPage";
import { TaxesSettingsPage } from "@/features/settings/TaxesSettingsPage";
import { PoliciesSettingsPage } from "@/features/settings/PoliciesSettingsPage";
import { ProfilePage } from "@/features/profile/ProfilePage";
import { MorePage } from "@/features/more/MorePage";
import { NotificationsPage } from "@/features/notifications/NotificationsPage";
import type { Stay } from "@/types/domain";
import { LoadingGrid } from "@/components/ui/StateView";

export function App() {
  const token = useAppStore((state) => state.token);
  const role = useAppStore((state) => state.user?.role);
  const mustResetPassword = useAppStore((state) => state.user?.mustResetPassword);
  const activeOwner = useAppStore((state) => state.activeOwner);
  const activeProperty = useAppStore((state) => state.activeProperty);

  // A persisted session is trusted at face value until some API call happens to fire - if the
  // account was deactivated while the token was still sitting in localStorage, the user would
  // otherwise see the full workspace shell before that first incidental request 401s them out.
  // Verify eagerly on load instead; api-client's 401 handler already calls clearSession().
  const sessionCheck = useQuery({
    queryKey: ["auth-me"],
    queryFn: () => api<{ id: string }>("/auth/me"),
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
  });

  if (!token) return <AuthEntry />;
  if (sessionCheck.isPending) return <LoadingGrid />;
  // Added by a property's owner with a temporary password: set their own first.
  if (mustResetPassword) return <FirstLoginPasswordPage />;

  if (!activeProperty) {
    return (
      <Routes>
        <Route path="/" element={role === "ADMIN" ? <OwnersPage initialOwnerId={activeOwner?.id} /> : <PropertyPickerPage />} />
        <Route path="setup" element={role === "ADMIN" ? <OwnerSetupPage /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const workspace = (
    <Routes>
      <Route element={<WorkspaceShell />}>
        <Route index element={<PropertyDashboardPage />} />
        <Route path="bookings" element={<ResourcePage type="bookings" />} />
        <Route path="guests" element={<ResourcePage type="guests" />} />
        <Route path="rooms" element={<ResourcePage type="rooms" />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="settlements" element={<SettlementsPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="activity" element={<ActivityPage />} />
        <Route path="police-register" element={<PoliceRegisterPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="more" element={<MorePage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="settings" element={role === "ADMIN" ? <SettingsLayout /> : <Navigate to="/" replace />}>
          <Route index element={<Navigate to="general" replace />} />
          <Route path="general" element={<GeneralSettingsPage />} />
          <Route path="categories" element={<CategoriesSettingsPage />} />
          <Route path="room-types" element={<RoomTypesSettingsPage />} />
          <Route path="rate-plans" element={<RatePlansSettingsPage />} />
          <Route path="services" element={<ServicesSettingsPage />} />
          <Route path="amenities" element={<AmenitiesSettingsPage />} />
          <Route path="taxes" element={<TaxesSettingsPage />} />
          <Route path="policies" element={<PoliciesSettingsPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  if (role === "OWNER") return <OwnerWorkspaceGate>{workspace}</OwnerWorkspaceGate>;

  return workspace;
}

function OwnerWorkspaceGate({ children }: { children: ReactNode }) {
  const activeProperty = useAppStore((state) => state.activeProperty);
  const enterProperty = useAppStore((state) => state.enterProperty);
  const exitWorkspace = useAppStore((state) => state.exitWorkspace);
  const properties = useQuery({ queryKey: ["properties", "mine"], queryFn: () => api<Stay[]>("/stays") });

  useEffect(() => {
    if (!properties.data || !activeProperty) return;
    const matchingProperty = properties.data.find((property) => property.id === activeProperty.id);
    if (matchingProperty) return;
    if (properties.data.length === 1) {
      enterProperty(null, { id: properties.data[0].id, name: properties.data[0].name, type: properties.data[0].type });
    } else {
      exitWorkspace();
    }
  }, [activeProperty, enterProperty, exitWorkspace, properties.data]);

  if (properties.isLoading) return <main className="page"><LoadingGrid /></main>;
  if (properties.isError) return <PropertyPickerPage />;
  if (!activeProperty) return <PropertyPickerPage />;
  if (properties.data && !properties.data.some((property) => property.id === activeProperty.id)) return <main className="page"><LoadingGrid /></main>;

  return children;
}

const WELCOME_SEEN_KEY = "darshan.welcomeSeen";

function readWelcomeSeen() {
  try { return window.localStorage.getItem(WELCOME_SEEN_KEY) === "1"; } catch { return false; }
}

// Signed-out entry: the Welcome carousel shows once on phones/tablets (<= 820px), then Login.
// Wide screens skip it - Login's left panel carries the same introduction there.
function AuthEntry() {
  const isNarrow = typeof window !== "undefined" && window.matchMedia("(max-width: 820px)").matches;
  const [step, setStep] = useState<"welcome" | "login">(() => (isNarrow && !readWelcomeSeen() ? "welcome" : "login"));
  const [welcomeShown, setWelcomeShown] = useState(step === "welcome");

  // Back from Login returns to Welcome only if Welcome was shown in this session.
  useBackCloseable(step === "login" && welcomeShown && isNarrow, () => setStep("welcome"));

  if (step === "welcome") {
    return (
      <WelcomePage
        onDone={() => {
          try { window.localStorage.setItem(WELCOME_SEEN_KEY, "1"); } catch { /* storage unavailable - Welcome may show again */ }
          setStep("login");
        }}
      />
    );
  }

  return <LoginPage onShowWelcome={isNarrow ? () => { setWelcomeShown(true); setStep("welcome"); } : undefined} />;
}
